class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('.cart-drawer__overlay').addEventListener('click', this.close.bind(this));
    if (this.querySelector('.cart-drawer__close')) {
      this.querySelector('.cart-drawer__close').addEventListener('click', this.close.bind(this));
    }

    // Handle clicks outside the drawer
    document.addEventListener('click', (evt) => {
      if (
        this.classList.contains('active') &&
        !evt.target.closest('.drawer__inner') &&
        !evt.target.closest('#cart-icon-bubble') &&
        !evt.target.closest('.cart-drawer__form') &&
        !evt.target.closest('.quantity-popover__info') &&
        !evt.target.closest('quantity-popover')
      ) {
        this.close();
      }
    });

    this.setHeaderCartIconAccessibility();

    // Subscribe to cart update events
    document.addEventListener('cart:updated', (event) => {
      this.updateCartIconBubble(event.detail.cartData);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  updateCartIconBubble(parsedState) {
    const cartToggle = document.querySelector('[data-drawer-toggle="cart"]');
    if (!cartToggle) return;

    if (parsedState.sections && parsedState.sections['cart-icon-bubble']) {
      const cartIconBubbleHtml = parsedState.sections['cart-icon-bubble'];
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = cartIconBubbleHtml;

      const newBubble = tempDiv.querySelector('.cart-count-bubble');
      const existingBubble = cartToggle.querySelector('.cart-count-bubble');
      if (newBubble) {
        if (existingBubble) {
          existingBubble.innerHTML = newBubble.innerHTML;
        } else {
          cartToggle.appendChild(newBubble);
        }
      } else {
        existingBubble?.remove();
      }
    }
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.updateCartIconBubble(parsedState);

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantSelectChange.bind(this));
  }

  // Prevent variant selects from reaching the quantity update path
  onChange(event) {
    if (event.target.matches('.cart-item__variant-select')) return;
    super.onChange(event);
  }

  // Treat quantity 0 as valid removal — skip validation error and reset
  validateQuantity(event) {
    if (parseInt(event.target.value) === 0) {
      event.target.setCustomValidity('');
      this.updateQuantity(event.target.dataset.index, 0);
      return;
    }
    super.validateQuantity(event);
  }

  onVariantSelectChange(event) {
    if (!event.target.matches('.cart-item__variant-select')) return;

    const row = event.target.closest('tr.cart-item');
    if (!row) return;

    const wrapper = row.querySelector('.cart-item__options--selects');
    const itemKey = wrapper.dataset.itemKey;
    const itemQuantity = parseInt(wrapper.dataset.itemQuantity, 10);

    const selects = [...wrapper.querySelectorAll('.cart-item__variant-select')];
    const selectedOptions = selects.map(s => s.value);

    const variants = JSON.parse(row.querySelector('.cart-item__variants-json').textContent);
    const matched = variants.find(v => selectedOptions.every((val, i) => v[`option${i + 1}`] === val));

    if (!matched) {
      this.showVariantError(row, 'Esta combinación no está disponible.');
      this.resetVariantSelect(event.target, wrapper);
      return;
    }
    if (!matched.available) {
      this.showVariantError(row, 'Esta variante está agotada.');
      this.resetVariantSelect(event.target, wrapper);
      return;
    }

    this.swapVariant(itemKey, itemQuantity, matched.id, wrapper.dataset.originalVariantId, row);
  }

  async swapVariant(itemKey, quantity, newVariantId, originalVariantId, row) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) cartDrawer.classList.add('loading');

    const selects = row?.querySelectorAll('.cart-item__variant-select') || [];
    selects.forEach(s => s.disabled = true);

    try {
      // Paso 1: eliminar item actual por key
      await fetch(window.routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({ id: itemKey, quantity: 0 })
      });

      // Paso 2: añadir nueva variante (cart/add.js devuelve sections)
      const addRes = await fetch(window.routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [{ id: newVariantId, quantity }],
          sections: cartDrawer.getSectionsToRender().map(s => s.section),
          sections_url: window.location.pathname
        })
      });
      const addState = await addRes.json();

      if (addState.status) {
        this.showVariantError(row, 'No se pudo cambiar la variante. Intenta de nuevo.');
        // Recuperar variante original
        await fetch(window.routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify({ items: [{ id: originalVariantId, quantity }] })
        });
        return;
      }

      if (cartDrawer) {
        cartDrawer.classList.toggle('is-empty', !addState.items?.length);
        cartDrawer.renderContents(addState);
      }
    } catch (err) {
      console.error('Error al cambiar variante:', err);
      this.showVariantError(row, window.cartStrings?.error || 'Error al actualizar el carrito.');
    } finally {
      if (cartDrawer) cartDrawer.classList.remove('loading');
      selects.forEach(s => s.disabled = false);
    }
  }

  showVariantError(row, message) {
    const errEl = row?.querySelector('.cart-item__error-text');
    if (!errEl) return;
    errEl.textContent = message;
    setTimeout(() => { errEl.textContent = ''; }, 3000);
  }

  resetVariantSelect(select, wrapper) {
    const idx = select.dataset.optionIndex;
    const original = wrapper.dataset[`currentOption${idx}`];
    if (original !== undefined) select.value = original;
  }

  async updateQuantity(line, quantity, name, variantId) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) cartDrawer.classList.add('loading');

    const body = JSON.stringify({
      line,
      quantity,
      sections: cartDrawer.getSectionsToRender().map((s) => s.section),
      sections_url: window.location.pathname
    });

    try {
      const response = await fetch(`${window.routes.cart_change_url}`, { ...fetchConfig(), ...{ body } });
      const parsedState = await response.json();

      if (parsedState.errors) {
        const input = document.getElementById(`Drawer-quantity-${line}`);
        if (input) input.value = input.getAttribute('value');
        return;
      }

      if (cartDrawer) {
        cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
        cartDrawer.renderContents(parsedState);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      if (cartDrawer) cartDrawer.classList.remove('loading');
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerRecommendations extends HTMLElement {
  connectedCallback() {
    const productId = this.dataset.productId;
    const limit = parseInt(this.dataset.limit || '6', 10);
    const cartIds = new Set((this.dataset.cartProductIds || '').split(',').filter(Boolean));

    // Estado colapsado/oculto persistente entre re-renders del carrito (se re-crea todo #CartDrawer).
    const details = this.closest('.cart-drawer__cross-sell');
    if (details) {
      try {
        if (localStorage.getItem('cefiba-crosssell-collapsed') === '1') details.open = false;
      } catch (e) {}
      details.addEventListener('toggle', () => {
        try { localStorage.setItem('cefiba-crosssell-collapsed', details.open ? '0' : '1'); } catch (e) {}
      });
    }

    // Productos que NO deben salir como recomendados en el carrito (son upsells de checkbox del producto).
    const EXCLUDED_HANDLES = new Set(['llave-kaba-junto-cilindro', 'copia-de-llave-keso-junto-cilindro']);
    const allow = (p) => !cartIds.has(String(p.id)) && !EXCLUDED_HANDLES.has(p.handle);

    const base = `/recommendations/products.json?product_id=${productId}&limit=${limit}`;
    fetch(`${base}&intent=complementary`)
      .then(r => r.json())
      .then(data => {
        const products = (data.products || []).filter(allow);
        if (products.length) return products;
        return fetch(`${base}&intent=related`).then(r => r.json()).then(d => (d.products || []).filter(allow));
      })
      .then(products => {
        if (!products.length) {
          this.closest('.cart-drawer__cross-sell')?.remove();
          return;
        }
        this.innerHTML = this.renderList(products);
        this.removeAttribute('hidden');
      })
      .catch(() => {
        this.closest('.cart-drawer__cross-sell')?.remove();
      });

    this.addEventListener('click', e => {
      const btn = e.target.closest('.cross-sell-card__add[data-variant-id]');
      if (!btn) return;
      this.addToCart(btn.dataset.variantId, btn);
    });
  }

  renderList(products) {
    return `<ul class="cross-sell-list" role="list">${products.map(p => this.renderCard(p)).join('')}</ul>`;
  }

  renderCard(product) {
    const variant = product.variants[0];
    const rawSrc = product.featured_image || product.images?.[0]?.src || '';
    const imgSrc = rawSrc.replace(/(\.[^.?]+)(\?.*)?$/, '_240x240$1$2');
    const price = this.formatMoney(variant.price);

    // Descuento (compare-at) → tachado + badge -X%
    const cmp = variant.compare_at_price;
    const hasDiscount = cmp && cmp > variant.price;
    const compareHtml = hasDiscount ? `<span class="cross-sell-card__compare">${this.formatMoney(cmp)}</span>` : '';
    const discount = hasDiscount ? Math.round(((cmp - variant.price) / cmp) * 100) : 0;
    const badge = discount >= 1 ? `<span class="cross-sell-card__badge">-${discount}%</span>` : '';

    // Botón "+" sobre la imagen: añade SIEMPRE al carrito directamente (primera variante disponible)
    const plusSvg = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';
    const addVariant = product.variants.find(v => v.available) || variant;
    const addBtn = `<button type="button" class="cross-sell-card__add" data-variant-id="${addVariant.id}" aria-label="Añadir ${this.escapeHtml(product.title)} al carrito">${plusSvg}</button>`;

    // Prueba social: 5 estrellas doradas (mismo icono star3 que las cards de producto)
    const star = '<svg viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6.62772 3.17381C7.68325 1.28027 8.211 0.333496 9.00008 0.333496C9.78917 0.333496 10.3169 1.28026 11.3724 3.1738L11.6455 3.66369C11.9455 4.20177 12.0954 4.47082 12.3293 4.64834C12.5632 4.82585 12.8543 4.89175 13.4368 5.02354L13.9672 5.14352C16.0168 5.6073 17.0417 5.83918 17.2856 6.62327C17.5294 7.40733 16.8307 8.22441 15.4333 9.85841L15.0718 10.2812C14.6747 10.7455 14.4762 10.9777 14.3868 11.2649C14.2976 11.5522 14.3276 11.8619 14.3876 12.4815L14.4422 13.0455C14.6535 15.2257 14.7592 16.3157 14.1208 16.8003C13.4824 17.2849 12.5228 16.8431 10.6037 15.9595L10.1072 15.7308C9.56183 15.4797 9.28917 15.3542 9.00008 15.3542C8.711 15.3542 8.43833 15.4797 7.893 15.7308L7.3965 15.9595C5.47732 16.8431 4.51773 17.2849 3.87937 16.8003C3.24102 16.3157 3.34665 15.2257 3.55792 13.0455L3.61257 12.4815C3.67261 11.8619 3.70262 11.5522 3.6133 11.2649C3.52398 10.9777 3.32544 10.7455 2.92835 10.2812L2.56683 9.85841C1.16946 8.22441 0.470766 7.40733 0.7146 6.62327C0.958433 5.83918 1.9833 5.6073 4.03303 5.14352L4.56332 5.02354C5.14579 4.89175 5.43702 4.82585 5.67087 4.64834C5.90471 4.47082 6.05469 4.20178 6.35464 3.66369L6.62772 3.17381Z" fill="#EDC82E"/></svg>';
    const stars = `<div class="cross-sell-card__stars" aria-hidden="true">${star.repeat(5)}</div>`;

    return `<li class="cross-sell-card">
      <div class="cross-sell-card__media">
        ${badge}
        <a href="${product.url}" class="cross-sell-card__image" tabindex="-1"><img src="${imgSrc}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="132" height="132"></a>
        ${addBtn}
      </div>
      <a href="${product.url}" class="cross-sell-card__title">${this.escapeHtml(product.title)}</a>
      ${stars}
      <div class="cross-sell-card__prices">
        <span class="cross-sell-card__price${hasDiscount ? ' cross-sell-card__price--sale' : ''}">${price}</span>
        ${compareHtml}
      </div>
    </li>`;
  }

  async addToCart(variantId, btn) {
    btn.disabled = true;
    const isIconBtn = btn.classList.contains('cross-sell-card__add');
    const originalHTML = btn.innerHTML;
    if (!isIconBtn) btn.textContent = '...';
    const cartDrawer = document.querySelector('cart-drawer');

    try {
      const res = await fetch(window.routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
          sections: cartDrawer?.getSectionsToRender().map(s => s.section),
          sections_url: window.location.pathname
        })
      });
      const state = await res.json();
      if (cartDrawer && !state.status) {
        cartDrawer.renderContents(state);
      } else {
        btn.disabled = false;
        btn.innerHTML = originalHTML;
      }
    } catch {
      btn.disabled = false;
      btn.innerHTML = originalHTML;
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
